import { NO_DATA_ERROR } from "@/shared/constants/errors";
import {
  DEFAULT_ERROR_TRANSLATION_KEY,
  DEFAULT_ERROR_TYPES,
  apiErrorToTranslationKey,
} from "./error-mapper";
import type { ErrorDisplayType } from "./error.types";

const HTTP_STATUS_CODE = {
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  SERVICE_UNAVAILABLE: 503,
} as const;

export type ApiErrorResponse = {
  code: string;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  statusCode: number;
  path: string;
  method: string;
};

export class HttpError extends Error {
  readonly originalError: unknown;
  readonly apiErrorResponse?: ApiErrorResponse;
  readonly unknownErrorResponse?: unknown;

  constructor(options: {
    message?: string;
    originalError: unknown;
    apiErrorResponse?: ApiErrorResponse;
    unknownErrorResponse?: unknown;
  }) {
    super(options.message);

    this.originalError = options.originalError;
    this.apiErrorResponse = options.apiErrorResponse;
    this.unknownErrorResponse = options.unknownErrorResponse;
  }

  public get errorCode(): string | undefined {
    if (!this.apiErrorResponse) {
      return undefined;
    }

    return this.apiErrorResponse.code;
  }

  public get statusCode(): number | undefined {
    if (!this.apiErrorResponse) {
      return undefined;
    }

    return this.apiErrorResponse.statusCode;
  }

  public get translationKeyAndDisplayTypes(): {
    translationKey: string;
    displayTypes: ErrorDisplayType[];
  } {
    if (this.errorCode && apiErrorToTranslationKey[this.errorCode]) {
      const { key, types } = apiErrorToTranslationKey[this.errorCode];
      return { translationKey: key, displayTypes: types };
    }

    return {
      translationKey: DEFAULT_ERROR_TRANSLATION_KEY,
      displayTypes: DEFAULT_ERROR_TYPES,
    };
  }

  public hasErrorDisplayType(type: ErrorDisplayType): boolean {
    const { displayTypes } = this.translationKeyAndDisplayTypes;
    return displayTypes.includes(type);
  }

  public get isServiceUnavailable(): boolean {
    return this.statusCode === HTTP_STATUS_CODE.SERVICE_UNAVAILABLE;
  }

  public get isAccessDenied(): boolean {
    return this.statusCode === HTTP_STATUS_CODE.FORBIDDEN;
  }

  public get isNotFound(): boolean {
    return this.statusCode === HTTP_STATUS_CODE.NOT_FOUND;
  }

  public get isConflict(): boolean {
    return this.statusCode === HTTP_STATUS_CODE.CONFLICT;
  }

  public get isNoDataError(): boolean {
    return this.message === NO_DATA_ERROR;
  }

  public get isFileTooLargeError(): boolean {
    return this.message.includes("File too large");
  }

  public get isUnsupportedContentTypeError(): boolean {
    return this.message.includes("Unsupported content type");
  }

  // @comment: could complete to centralize toastr logics
  // @comment: or could create a new class new HttpToastrError(httpError)
}
